import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { AuthMobileLogo, AuthRightPanel } from "@/components/auth/auth-layout";
import { LeftPanel } from "@/components/layout/left-panel";
import {
  ShieldCheck,
  Eye,
  EyeOff,
  UserPlus,
  Lock,
  Database,
  Server,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { isFreeDesktopEdition } from "@/lib/edition/edition";

const SETUP_BRANDING = {
  onprem: {
    badge: "Primera instalación",
    headline: "Configura tu servidor Heskala",
    subtitle:
      "Esta es la primera vez que se abre Heskala en este equipo. Crea la cuenta del administrador para empezar a operar localmente.",
    heading: "Bienvenido a Heskala On-prem",
    features: [
      { icon: Lock, label: "Datos aislados en tu servidor" },
      { icon: Database, label: "Base de datos propia" },
      { icon: Server, label: "Sin dependencia de la nube" },
      { icon: KeyRound, label: "Activado por licencia firmada" },
    ],
  },
  free_desktop: {
    badge: "Primera instalación",
    headline: "Configura Heskala Free",
    subtitle:
      "Esta es la primera vez que abrís Heskala Free en este equipo. Crea la cuenta del administrador para empezar a operar 100% local.",
    heading: "Bienvenido a Heskala Free",
    features: [
      { icon: Lock, label: "100% local en tu equipo" },
      { icon: Database, label: "Tus datos nunca salen de tu computadora" },
      { icon: Server, label: "Sin Docker ni dependencias extra" },
      { icon: KeyRound, label: "Activado con token gratuito" },
    ],
  },
};

export const Route = createFileRoute("/_public/setup")({
  component: SetupPage,
});

// Pantalla de primer arranque (solo on-prem, base sin usuarios). Crea el
// primer administrador vía auth signUp. Tras crearlo, el AuthGuardCombined
// detecta "autenticado sin empresa" en on-prem y muestra el onboarding, que
// aprovisiona la empresa con el tier de la licencia.
function SetupPage() {
  const navigate = useNavigate();
  const { signIn } = useAuthActions();
  const brand = isFreeDesktopEdition()
    ? SETUP_BRANDING.free_desktop
    : SETUP_BRANDING.onprem;
  const bootstrapAvailable = useQuery(
    api.system.onboarding.queries.isBootstrapAvailable,
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // La ruta solo existe durante el primer arranque. Si ya hay un admin (o no
  // es on-prem), el bootstrap no está disponible → al login.
  useEffect(() => {
    if (bootstrapAvailable === false) {
      navigate({ to: "/auth/sign-in", replace: true });
    }
  }, [bootstrapAvailable, navigate]);

  if (bootstrapAvailable === undefined || bootstrapAvailable === false) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      toast.error("Ingresa el nombre del administrador");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Ingresa un correo electrónico válido");
      return;
    }
    if (password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setSubmitting(true);
    try {
      const created = await signIn("password", {
        name: name.trim(),
        email: email.trim(),
        password,
        flow: "signUp",
      });
      if (!created) {
        toast.error(
          "No se pudo crear la cuenta de administrador. Verifica los datos e intenta nuevamente.",
        );
        setSubmitting(false);
        return;
      }
      // Autenticado y sin empresa → el guard mostrará el onboarding.
      navigate({ to: "/dashboard", replace: true });
    } catch (err) {
      console.error("Error creando administrador:", err);
      toast.error(
        "No se pudo crear la cuenta de administrador. Intenta nuevamente.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <LeftPanel
        badge={brand.badge}
        headline={brand.headline}
        subtitle={brand.subtitle}
        features={brand.features}
      />

      <AuthRightPanel>
        <div className="mb-8 text-center lg:text-left">
          <AuthMobileLogo />
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            {brand.heading}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Crea el primer usuario administrador de esta instalación.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle>Datos del administrador</CardTitle>
            </div>
            <CardDescription>
              Esta cuenta tendrá control total sobre la instalación.
            </CardDescription>
          </CardHeader>

          <form onSubmit={onSubmit} className="flex flex-col gap-2">
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del administrador</Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Juan Pérez"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Correo del administrador</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@empresa.com"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    disabled={submitting}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={submitting}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Confirmar contraseña</Label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repite tu contraseña"
                  disabled={submitting}
                />
              </div>
            </CardContent>

            <CardFooter>
              <Button
                type="submit"
                className="w-full"
                disabled={
                  submitting || !name || !email || !password || !confirm
                }
              >
                {submitting ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Creando administrador…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Crear administrador
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </AuthRightPanel>
    </div>
  );
}
